import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  BellOff,
  CheckCheck,
  CreditCard,
  LifeBuoy,
  Megaphone,
  ReceiptText,
  Vote,
} from 'lucide-react';
import { describeApiError, notificationsApi } from '@/api';
import type { AppNotification, NotificationKind, ServerClock } from '@/api/types';
import { useLocalizedName } from '@/lib/useLocalizedName';

const ICONS: Record<NotificationKind, typeof Bell> = {
  announcement: Megaphone,
  payment: ReceiptText,
  ticket: LifeBuoy,
  subscription: CreditCard,
  invoice: ReceiptText,
  poll: Vote,
  system: Bell,
};

/**
 * The bell. Previously it was a button with no handler — it rendered, it
 * looked live, and nothing happened on any of the three role dashboards.
 *
 * The panel is anchored to the button rather than dropped at the bottom of the
 * document, closes on outside click and on Escape, and marks a row read the
 * moment it is opened rather than on a separate action nobody performs.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const localized = useLocalizedName();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [clock, setClock] = useState<ServerClock | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const wrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const feed = await notificationsApi.feed({ page: 1, pageSize: 20 });
      setItems(feed.items);
      setUnread(feed.unreadCount);
      setClock(feed.clock);
      setError('');
    } catch (cause) {
      setError(describeApiError(cause, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // The badge must be right before the panel is ever opened, so the count is
  // fetched on mount and refreshed quietly while the tab is in the foreground.
  useEffect(() => {
    void load();
    const handle = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 60_000);
    return () => clearInterval(handle);
  }, [load]);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPanel = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const openItem = async (item: AppNotification) => {
    if (!item.isRead) {
      setItems((rows) => rows.map((row) => (row.id === item.id ? { ...row, isRead: true } : row)));
      setUnread((count) => Math.max(0, count - 1));
      await notificationsApi.markRead(item.id).catch(() => undefined);
    }
    if (item.link) {
      setOpen(false);
      navigate(item.link);
    }
  };

  const markAll = async () => {
    setItems((rows) => rows.map((row) => ({ ...row, isRead: true })));
    setUnread(0);
    await notificationsApi.markAllRead().catch(() => undefined);
  };

  return (
    <div className="notification-wrap" ref={wrapRef}>
      <button
        type="button"
        className="icon-button notification-button"
        aria-label={t('app.notifications')}
        aria-expanded={open}
        onClick={openPanel}
      >
        <Bell size={19} />
        {unread > 0 && <span className="notification-dot">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="notification-panel" role="dialog" aria-label={t('app.notifications')}>
          <header>
            <b>{t('app.notifications')}</b>
            {unread > 0 && (
              <button type="button" className="text-link" onClick={() => void markAll()}>
                <CheckCheck size={14} /> {t('notifications.markAll')}
              </button>
            )}
          </header>

          {error && <p className="notification-empty">{error}</p>}

          {!error && loading && items.length === 0 && (
            <p className="notification-empty">{t('common.loading')}</p>
          )}

          {!error && !loading && items.length === 0 && (
            <p className="notification-empty">
              <BellOff size={22} aria-hidden />
              {t('notifications.empty')}
            </p>
          )}

          <ul>
            {items.map((item) => {
              const Icon = ICONS[item.kind] ?? Bell;
              return (
                <li key={item.id} className={item.isRead ? undefined : 'unread'}>
                  <button type="button" onClick={() => void openItem(item)}>
                    <span className="notification-icon">
                      <Icon size={16} aria-hidden />
                    </span>
                    <span className="notification-text">
                      <b>{localized(item.titleEn, item.titleAr)}</b>
                      <small>{localized(item.bodyEn, item.bodyAr)}</small>
                      <time>{relativeTime(item.createdAtUtc, clock, t)}</time>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * "منذ ساعتين" measured against the *server's* clock, not the device's.
 * A phone with the wrong date must not be able to change what the app says
 * about when something happened.
 */
/* مُصدَّرة لأن صفحة الإشعارات تحتاجها أيضاً. نسخُها هناك يعني تعريفين
   ينحرفان — وهو مصدر أكثر أخطاء هذا المشروع. */
export function relativeTime(
  iso: string,
  clock: ServerClock | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const now = clock ? Date.parse(clock.nowUtc) : Date.now();
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));

  if (minutes < 1) return t('time.justNow');
  if (minutes < 60) return t('time.minutesAgo', { count: minutes });

  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('time.hoursAgo', { count: hours });

  return t('time.daysAgo', { count: Math.round(hours / 24) });
}
